import L, { type LatLngBoundsExpression, type LatLngTuple } from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useEffect } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import type { PublicListingDto } from 'shared';
import { MapListingPopup } from './MapListingPopup.js';

/**
 * Leaflet + OpenStreetMap tiles — free, no API key, per this session's
 * scope decision over Google Maps. Plain individual pins, not clustering:
 * at the scale CLAUDE.md names for this whole feature (10-30 active
 * listings), cluster bubbles are complexity the pin count doesn't justify.
 * The reference screenshot's "N units" bubbles are a Google Maps artifact,
 * not a re-stated requirement.
 *
 * Leaflet's default marker icon paths break under a bundler unless
 * explicitly re-pointed at the package's own asset URLs — a known
 * Leaflet+Vite/webpack integration wrinkle, fixed once here at module scope
 * rather than discovered per-render.
 *
 * Clicking a pin opens `MapListingPopup` — a small card with a photo,
 * price, and specs, not just plain text — with a link into the listing's
 * own detail page (`/listings/:id`).
 */
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Metro Detroit — CLAUDE.md's stated market — used when no listing has a geocode yet. */
const DETROIT_CENTER: LatLngTuple = [42.3314, -83.0458];
const DEFAULT_ZOOM = 10;
/** Caps how far a single pin (or a tight cluster) zooms in — street-level, not parcel-level. */
const MAX_FIT_ZOOM = 15;

/**
 * Re-fits the map to `bounds` on every change, not just on mount.
 *
 * `MapContainer`'s own `center`/`zoom`/`bounds` props are read once, at
 * mount, in react-leaflet — changing them on a later render (e.g. the
 * Rental Finder page's filters producing a different set of pins) does
 * nothing on its own. This is the imperative escape hatch react-leaflet
 * expects for that: a child with `useMap()`, calling `fitBounds` inside an
 * effect keyed on the bounds themselves.
 */
function FitBounds({ bounds }: { bounds: LatLngBoundsExpression | null }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [32, 32], maxZoom: MAX_FIT_ZOOM });
    } else {
      map.setView(DETROIT_CENTER, DEFAULT_ZOOM);
    }
  }, [map, bounds]);

  return null;
}

export function ListingsMap({ listings }: { listings: PublicListingDto[] }) {
  const pinned = listings.filter(
    (listing): listing is PublicListingDto & { latitude: string; longitude: string } =>
      listing.latitude !== null && listing.longitude !== null,
  );

  const bounds: LatLngTuple[] | null =
    pinned.length > 0 ? pinned.map((l) => [Number(l.latitude), Number(l.longitude)]) : null;

  return (
    <div className="public__map">
      <MapContainer
        center={DETROIT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds bounds={bounds} />
        {pinned.map((listing) => (
          <Marker key={listing.id} position={[Number(listing.latitude), Number(listing.longitude)]}>
            <Popup minWidth={220} maxWidth={240}>
              <MapListingPopup listing={listing} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
